import { useEffect, useState } from 'react';
import { ArrowLeft, Save, Warehouse } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import localService, { type LocalInput, type LocalTipo } from '@/services/local';

const TIPO_LABELS: Record<LocalTipo, string> = {
  ARMAZEM: 'Armazém',
  DEPOSITO: 'Depósito',
  LINHA_PRODUCAO: 'Linha de Produção',
  EXPEDICAO: 'Expedição',
  QUARENTENA: 'Quarentena',
  REFUGO: 'Refugo',
  OUTRO: 'Outro',
};

const FORM_VAZIO: LocalInput = {
  codigo: '',
  descricao: '',
  tipo: 'ARMAZEM',
  capacidade: null,
  unidade_capacidade: '',
  observacoes: '',
  ativo: true,
};

export default function LocalForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<LocalInput>(FORM_VAZIO);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    localService.buscarPorId(id)
      .then((data) => setForm({
        codigo: data.codigo,
        descricao: data.descricao,
        tipo: data.tipo,
        capacidade: data.capacidade ?? null,
        unidade_capacidade: data.unidade_capacidade || '',
        observacoes: data.observacoes || '',
        ativo: data.ativo,
      }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (!form.descricao || !form.tipo) {
      setErro('Informe a descrição e o tipo do local.');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      if (id) {
        const payload = {
          descricao: form.descricao,
          tipo: form.tipo,
          capacidade: form.capacidade,
          unidade_capacidade: form.unidade_capacidade,
          observacoes: form.observacoes,
          ativo: form.ativo,
        };
        await localService.atualizar(id, payload);
      } else {
        await localService.criar(form);
      }
      navigate('/producao/locais');
    } catch (error) {
      console.error('Erro ao salvar local', error);
      setErro('Não foi possível salvar o local. Verifique os dados e tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<Warehouse size={21} strokeWidth={2} />}
        title={editando ? 'Editar Local' : 'Novo Local'}
        description={editando ? 'Atualize os dados cadastrais do local.' : 'Preencha os dados para incluir um novo local.'}
        actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/locais')}><ArrowLeft size={16} /> Voltar</Button>}
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        {erro ? <div className="nx-sngpc-error" role="alert">{erro}</div> : null}

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Identificação</h2>
          </div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Código *" htmlFor="local-codigo">
              <Input id="local-codigo" value={editando ? form.codigo || '' : 'Gerado ao salvar'} readOnly disabled={loading} />
            </Field>
            <Field label="Tipo *" htmlFor="local-tipo">
              <Select id="local-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as LocalTipo })} disabled={loading} required>
                {Object.entries(TIPO_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </Field>
            <Field label="Descrição *" htmlFor="local-descricao" className="nx-sngpc-field--full">
              <Input id="local-descricao" value={form.descricao || ''} onChange={(e) => setForm({ ...form, descricao: e.target.value })} disabled={loading} required />
            </Field>
          </div>
        </section>

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Capacidade</h2>
          </div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Capacidade" htmlFor="local-capacidade">
              <DecimalInput id="local-capacidade" value={form.capacidade ?? null} onValueChange={(value) => setForm({ ...form, capacidade: value })} disabled={loading} />
            </Field>
            <Field label="Unidade" htmlFor="local-unidade">
              <Input id="local-unidade" value={form.unidade_capacidade || ''} onChange={(e) => setForm({ ...form, unidade_capacidade: e.target.value })} disabled={loading} />
            </Field>
            <Field label="Observações" htmlFor="local-observacoes" className="nx-sngpc-field--full">
              <Textarea id="local-observacoes" value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} disabled={loading} />
            </Field>
            <label className="nx-sngpc-checkbox-field">
              <Checkbox checked={form.ativo ?? true} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} disabled={loading} />
              <span>Ativo</span>
            </label>
          </div>
        </section>
      </Card>

      <FormStickyFooter
        cancelLabel="Cancelar"
        onCancel={() => navigate('/producao/locais')}
        saveLabel="Salvar"
        loading={salvando}
        saveIcon={<Save size={16} />}
      />
    </form>
  );
}
